import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import {
  IconPackageExport,
  IconPackages,
  IconRoute,
  IconPackage,
  IconTruck,
  IconPackageImport,
  IconPackageOff,
  IconBuildingStore,
  IconAdjustments,
  IconSettings,
  IconClipboardCheck,
  IconClipboardX,
  IconLockOpen,
  IconUserCheck,
  IconTruckDelivery,
  IconTags,
  IconClipboardList,
  IconHourglass,
  IconReceipt,
  IconArrowBackUp,
  IconCurrentLocation,
  IconSignature,
  IconBuildingWarehouse,
  IconAnchor,
} from '@tabler/icons-react';
import { ChevronRight, Loader2, Signpost } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import { Ci2HelpHexIcon } from '../../process-config/components/Ci2HelpHexIcon';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import { GoHoverTooltip } from './GoHoverTooltip';
import GoRaioKmSlider, { normalizeMetros } from './GoRaioKmSlider';
import '../../process-config/ConfigStatusView2.css';
import './GoProcessosConfig.css';

const API_BASE_URL = getApiBaseUrl();

const FLUXO_OS_FIELDS = [
  {
    key: 'aguardando_atendimento',
    label: 'Aguardando atendimento',
    kind: 'alnum',
    len: 2,
    Icon: IconHourglass,
    info:
      'Quando houver deslocamento para mais de uma OS no mesmo endereço, ao iniciar o primeiro atendimento as demais ordens não devem continuar com o status Em deslocamento. Por isso, elas serão automaticamente movidas para um status que indica que estão aguardando em fila para serem atendidas no local.',
  },
  {
    key: 'aguardando_assinatura',
    label: 'Aguardando assinatura',
    kind: 'alnum',
    len: 2,
    Icon: IconSignature,
    info: 'Quando atendimentos em lote estão aguardando assinatura.',
  },
];

const FLUXO_FIELDS = [
  { key: 'roteirizado', label: 'Roteirizado', kind: 'alnum', len: 2, Icon: IconRoute },
  { key: 'coletado', label: 'Coletado', kind: 'alnum', len: 2, Icon: IconPackage },
  { key: 'em_deslocamento', label: 'Em deslocamento', kind: 'alnum', len: 2, Icon: IconTruck },
  { key: 'entregue', label: 'Entregue', kind: 'alnum', len: 2, Icon: IconPackageImport },
  { key: 'nao_entregue', label: 'Não entregue', kind: 'alnum', len: 2, Icon: IconPackageOff },
];

const FLUXO_RETIRADA_FIELDS = [
  {
    key: 'roteirizado',
    label: 'Roteirizado',
    kind: 'alnum',
    len: 2,
    Icon: IconRoute,
    info: 'Retirada liberada, o motorista pode ir',
  },
  {
    key: 'em_deslocamento',
    label: 'Em deslocamento',
    kind: 'alnum',
    len: 2,
    Icon: IconTruck,
    info: 'Motorista a caminho, ato de iniciar o deslocamento',
  },
  {
    key: 'retirada_convergente',
    label: 'Retirada convergente',
    kind: 'alnum',
    len: 2,
    Icon: IconClipboardCheck,
    info: 'Qual status gravar quando retirou tudo como previsto',
  },
  {
    key: 'retirada_divergente',
    label: 'Retirada divergente',
    kind: 'alnum',
    len: 2,
    Icon: IconClipboardX,
    info: 'Qual status gravar quando faltou item, sobrou item ou entrou série não prevista',
  },
  {
    key: 'retirada_nao_realizada',
    label: 'Retirada não realizada',
    kind: 'alnum',
    len: 2,
    Icon: IconPackageOff,
    info: 'Qual status gravar quando não retirou nada, com motivo',
  },
];

const PROCESS_TABS = [
  { id: 'os', label: 'Fluxo de OS' },
  { id: 'entrega', label: 'Fluxo de entrega' },
  { id: 'retirada', label: 'Fluxo de retirada' },
  { id: 'kit', label: 'Kit técnico' },
  { id: 'estoque', label: 'Estoque' },
];

const PEDIDO_CLIENTE_FIELDS = [
  { key: 'condicao', label: 'Condição', kind: 'alnum', len: 3, Icon: IconAdjustments },
  { key: 'operacao', label: 'Operação', kind: 'alnum', len: 2, Icon: IconSettings },
  { key: 'status', label: 'Status', kind: 'alnum', len: 2, Icon: IconClipboardCheck },
  { key: 'transportadora', label: 'Transportadora', kind: 'alnum', len: 4, Icon: IconTruckDelivery },
  { key: 'classificacao', label: 'Classificação', kind: 'alnum', len: 4, Icon: IconTags },
];

const ROLLBACK_KIT_FIELDS = [
  { key: 'fornecedor', label: 'Fornecedor', kind: 'alnum', len: 4, Icon: IconBuildingStore },
  {
    key: 'operacao',
    label: 'Operação',
    kind: 'alnum',
    len: 2,
    Icon: IconSettings,
    info: 'Operação de compra que o pedido assumirá',
  },
  {
    key: 'condicao',
    label: 'Condição',
    kind: 'alnum',
    len: 3,
    Icon: IconAdjustments,
    info: 'Condição de pagamento para o pedido',
  },
  {
    key: 'status',
    label: 'Status',
    kind: 'alnum',
    len: 2,
    Icon: IconClipboardCheck,
    info: 'Definir status do processo de entrada, em que o pedido de entrada assumirá.',
  },
  {
    key: 'transportadora',
    label: 'Transportadora',
    kind: 'alnum',
    len: 4,
    Icon: IconTruckDelivery,
    info: 'Transportadora  para o pedido',
  },
];

const KIT_FIELDS = [
  { key: 'cod_cliente', label: 'Código de cliente', kind: 'num', len: 8, Icon: IconBuildingStore },
  {
    key: 'condicao',
    label: 'Condição',
    kind: 'num',
    len: 3,
    Icon: IconAdjustments,
    info: 'Campo condição de recebimento no pedido',
  },
  {
    key: 'operacao',
    label: 'Operação',
    kind: 'alnum',
    len: 2,
    Icon: IconSettings,
    info: 'Código da operação de saída de dois dígitos no pedido.',
  },
  {
    key: 'st_kit_pend_disp',
    label: 'Status - Kit pendente',
    stockTag: 'disponível no estoque',
    kind: 'alnum',
    len: 2,
    Icon: IconClipboardCheck,
    info: 'Status do pedido de reposição de Kit, onde ainda está aguardando andamento.',
  },
  {
    key: 'st_kit_pend_indisp',
    label: 'Status - Kit pendente',
    stockTag: 'indisponível no estoque',
    kind: 'alnum',
    len: 2,
    Icon: IconClipboardX,
    info: 'Status do pedido de reposição de Kit, onde ainda está aguardando andamento.',
  },
  {
    key: 'st_kit_liberado',
    label: 'Status - Kit liberado',
    kind: 'alnum',
    len: 2,
    Icon: IconLockOpen,
    info: 'Status do pedido de reposição de Kit, onde está liberado para coleta.',
  },
  {
    key: 'st_kit_recebido_tec',
    label: 'Status - Recebido pelo técnico',
    kind: 'alnum',
    len: 2,
    Icon: IconUserCheck,
    info: 'O pedido do Kit foi coletado pelo técnico, processo finaliza.',
  },
  {
    key: 'transportadora',
    label: 'Transportadora',
    kind: 'alnum',
    len: 4,
    Icon: IconTruckDelivery,
    info: 'Código da transportadora  de quatro dígitos no pedido.',
  },
  {
    key: 'classificacao',
    label: 'Classificação',
    kind: 'alnum',
    len: 4,
    Icon: IconTags,
    info: 'Código da classificação (Class.), de quatro dígitos no pedido.',
  },
  {
    key: 'cod_tec_ancora',
    label: 'Técnico âncora',
    kind: 'alnum',
    len: 4,
    Icon: IconAnchor,
    info: 'Técnico do ERP usado nos pedidos de kit de técnicos cadastrados só no Vervify. O dono real do pedido fica no Vervify. Não use um técnico que atende OS nem com sobrescrita de kit.',
  },
];

function emptyDraft() {
  return {
    fluxo_os: Object.fromEntries(FLUXO_OS_FIELDS.map((f) => [f.key, ''])),
    raio_atendimento: { sem_raio: '0', distancia: '1' },
    fluxo_entrega: {
      ...Object.fromEntries(FLUXO_FIELDS.map((f) => [f.key, ''])),
      modo_conferencia: 'item',
    },
    fluxo_retirada: Object.fromEntries(FLUXO_RETIRADA_FIELDS.map((f) => [f.key, ''])),
    kit_tecnico: Object.fromEntries(KIT_FIELDS.map((f) => [f.key, ''])),
    pedido_cliente: Object.fromEntries(PEDIDO_CLIENTE_FIELDS.map((f) => [f.key, ''])),
    rollback_kit: Object.fromEntries(ROLLBACK_KIT_FIELDS.map((f) => [f.key, ''])),
  };
}

function sanitizeInput(raw, kind, len) {
  const s = String(raw ?? '').toUpperCase();
  const clean = kind === 'num' ? s.replace(/[^0-9]/g, '') : s.replace(/[^0-9A-Z]/g, '');
  return clean.slice(0, len);
}

function pinWidthForLen(len) {
  if (len >= 8) return 78;
  if (len >= 4) return 52;
  if (len >= 3) return 44;
  return 38;
}

function fieldAriaLabel(field) {
  if (field.stockTag) return `${field.label} (${field.stockTag})`;
  return field.label;
}

function Pcs2Switch({ checked, onChange, disabled, ariaLabel, tooltip }) {
  const switchEl = (
    <label className={`pcs2-switch${disabled ? ' pcs2-switch--disabled' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );

  if (!tooltip) return switchEl;

  return (
    <GoHoverTooltip text={tooltip} align="end" wrap>
      {switchEl}
    </GoHoverTooltip>
  );
}

function Pcs2SettingRow({ title, desc, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? <span className="pcs2-setting-desc">{desc}</span> : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function Pcs2Subblock({ title, desc, action, children, hideHead = false }) {
  return (
    <div className="pcs2-subblock">
      {!hideHead && (title || desc || action) ? (
        <div className={`pcs2-subhead${action ? ' pcs2-subhead--with-action' : ''}`}>
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
          {action ? <div className="pcs2-subhead-action">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Pcs2Section({ icon: Icon, title, desc, children }) {
  return (
    <section className="pcs2-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} stroke={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        <p className="pcs2-section-desc">{desc}</p>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function GoProcInfoTip({ text }) {
  if (!text) return null;
  return (
    <GoHoverTooltip text={text} align="center" wrap>
      <span className="pcs2-info-tip-host go-proc-info-tip" tabIndex={0} aria-label={text}>
        <Ci2HelpHexIcon label="Mais informações" />
      </span>
    </GoHoverTooltip>
  );
}

function Pcs2ListItem({
  icon: Icon,
  label,
  stockTag,
  info,
  id,
  value,
  onChange,
  disabled,
  maxLength = 2,
  ariaLabel,
}) {
  return (
    <div className={`pcs2-list-item${disabled ? ' pcs2-list-item--disabled' : ''}`}>
      <span className="pcs2-list-icon">
        <Icon size={12} stroke={1.75} aria-hidden />
      </span>
      <span className="pcs2-list-label go-proc-list-label">
        <span className="go-proc-list-label-text">{label}</span>
        {stockTag ? (
          <span className="go-proc-stock-tag">
            <span className="go-proc-stock-tag-label">{stockTag}</span>
          </span>
        ) : null}
        <GoProcInfoTip text={info} />
      </span>
      <input
        type="text"
        id={id}
        className="pcs2-list-pin go-proc-pin"
        style={{ width: pinWidthForLen(maxLength) }}
        maxLength={maxLength}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel || label}
        autoComplete="off"
        spellCheck={false}
      />
      <ChevronRight className="pcs2-list-chev" aria-hidden />
    </div>
  );
}

function draftsEqual(a, b) {
  if (!a || !b) return false;
  for (const group of [
    'fluxo_os',
    'raio_atendimento',
    'fluxo_entrega',
    'fluxo_retirada',
    'kit_tecnico',
    'pedido_cliente',
    'rollback_kit',
  ]) {
    const keys = Object.keys(a[group] || {});
    for (const key of keys) {
      if (String(a[group][key] || '') !== String(b[group]?.[key] || '')) return false;
    }
  }
  return true;
}

function applyPayloadToDraft(data) {
  const next = emptyDraft();
  FLUXO_OS_FIELDS.forEach((f) => {
    next.fluxo_os[f.key] = String(data.fluxo_os?.[f.key] ?? '');
  });
  next.raio_atendimento.sem_raio =
    String(data.raio_atendimento?.sem_raio ?? '0').trim() === '1' ? '1' : '0';
  next.raio_atendimento.distancia = String(normalizeMetros(data.raio_atendimento?.distancia));
  FLUXO_FIELDS.forEach((f) => {
    next.fluxo_entrega[f.key] = String(data.fluxo_entrega?.[f.key] ?? '');
  });
  next.fluxo_entrega.modo_conferencia =
    data.fluxo_entrega?.modo_conferencia === 'documento' ? 'documento' : 'item';
  FLUXO_RETIRADA_FIELDS.forEach((f) => {
    next.fluxo_retirada[f.key] = String(data.fluxo_retirada?.[f.key] ?? '');
  });
  KIT_FIELDS.forEach((f) => {
    next.kit_tecnico[f.key] = String(data.kit_tecnico?.[f.key] ?? '');
  });
  PEDIDO_CLIENTE_FIELDS.forEach((f) => {
    next.pedido_cliente[f.key] = String(data.pedido_cliente?.[f.key] ?? '');
  });
  ROLLBACK_KIT_FIELDS.forEach((f) => {
    next.rollback_kit[f.key] = String(data.rollback_kit?.[f.key] ?? '');
  });
  return next;
}

export default function GoProcessosConfig() {
  const [tab, setTab] = useState('os');
  const [draft, setDraft] = useState(emptyDraft);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();

  const loadConfig = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/config/go/process`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await res.json();
      if (!result.success) {
        toastError(result.message || 'Erro ao carregar processos Go');
        const empty = emptyDraft();
        setDraft(empty);
        setSavedSnapshot(empty);
        return;
      }
      const next = applyPayloadToDraft(result.data || {});
      setDraft(next);
      setSavedSnapshot(next);
    } catch {
      toastError('Erro ao carregar configuração de processos Go');
    } finally {
      setIsLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const isDirty = useMemo(
    () => savedSnapshot !== null && !draftsEqual(draft, savedSnapshot),
    [draft, savedSnapshot]
  );

  const handleChange = (group, field) => (e) => {
    const nextVal = sanitizeInput(e.target.value, field.kind, field.len);
    setDraft((prev) => ({
      ...prev,
      [group]: { ...prev[group], [field.key]: nextVal },
    }));
  };

  const handleRaioMetrosChange = (metros) => {
    setDraft((prev) => ({
      ...prev,
      raio_atendimento: { ...prev.raio_atendimento, distancia: String(normalizeMetros(metros)) },
    }));
  };

  const handleSemRaioChange = (e) => {
    // Switch ON = permite fora do raio → sem_raio=1; OFF = exige raio → sem_raio=0
    const next = e.target.checked ? '1' : '0';
    setDraft((prev) => ({
      ...prev,
      raio_atendimento: { ...prev.raio_atendimento, sem_raio: next },
    }));
  };

  const permiteForaDoRaio = String(draft.raio_atendimento?.sem_raio ?? '0') === '1';

  const handleModoConferenciaChange = (e) => {
    const next = e.target.checked ? 'documento' : 'item';
    setDraft((prev) => ({
      ...prev,
      fluxo_entrega: { ...prev.fluxo_entrega, modo_conferencia: next },
    }));
  };

  const conferenciaPorDocumento = draft.fluxo_entrega?.modo_conferencia === 'documento';

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/go/process`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(draft),
      });
      const result = await res.json();
      if (result.success) {
        const next = applyPayloadToDraft(result.data || draft);
        setDraft(next);
        setSavedSnapshot(next);
        toastSuccess(result.message || 'Configuração salva com sucesso!');
      } else {
        toastError(result.message || 'Erro ao salvar configuração');
      }
    } catch {
      toastError('Erro ao salvar configuração de processos Go');
    } finally {
      setIsSaving(false);
    }
  };

  const tabBar = (
    <div className="go-processos-segment-bar">
      <div className="go-processos-segment" role="tablist" aria-label="Seções de processos">
        {PROCESS_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className="go-processos-segment-btn"
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="pcs2-page go-processos-page">
        <section className="pcs2-section go-processos-section">
          <div className="pcs2-section-aside">
            <div className="pcs2-section-heading">
              <Signpost size={16} strokeWidth={1.75} aria-hidden />
              <h3 className="pcs2-section-title">Processos</h3>
            </div>
          </div>
          {tabBar}
          <div className="go-processos-surface">
            <ProcessConfigSkeleton
              variant="listBody"
              ariaLabel="Carregando processos do Go"
            />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pcs2-page go-processos-page">
      <section className="pcs2-section go-processos-section">
        <div className="pcs2-section-aside">
          <div className="pcs2-section-heading">
            <Signpost size={16} strokeWidth={1.75} aria-hidden />
            <h3 className="pcs2-section-title">Processos</h3>
          </div>
        </div>

        {tabBar}

        <div className="go-processos-surface">
          {tab === 'os' && (
            <>
              <Pcs2Section
                icon={IconClipboardList}
                title="Fluxo de OS"
                desc="Configuração de status do processo de ordens de serviço pelo App Go"
              >
                <Pcs2Subblock
                  title="Status do fluxo"
                  desc="Defina o código de cada etapa do fluxo de OS (2 caracteres alfanuméricos)"
                >
                  <div className="pcs2-card">
                    <div className="pcs2-list">
                      {FLUXO_OS_FIELDS.map((field) => (
                        <Pcs2ListItem
                          key={field.key}
                          id={`go-proc-os-${field.key}`}
                          icon={field.Icon}
                          label={field.label}
                          info={field.info}
                          value={draft.fluxo_os[field.key]}
                          onChange={handleChange('fluxo_os', field)}
                          disabled={isSaving}
                          maxLength={field.len}
                        />
                      ))}
                    </div>
                  </div>
                </Pcs2Subblock>
              </Pcs2Section>

              <hr className="pcs2-divider" />

              <Pcs2Section
                icon={IconCurrentLocation}
                title="Raio de atendimento"
                desc="Defina a distância mínima (em metros) entre o técnico e o local do atendimento para que ele possa começar iniciar o atendimento"
              >
                <Pcs2Subblock
                  title="Distância mínima"
                  desc="Selecione o raio em metros (1 a 1000 m) necessário para liberar o início do atendimento"
                >
                  <div className="pcs2-card go-raio-km-card">
                    <GoRaioKmSlider
                      value={draft.raio_atendimento.distancia}
                      onChange={handleRaioMetrosChange}
                      disabled={isSaving}
                    />
                  </div>
                  <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
                    <Pcs2SettingRow
                      title="Permitir atendimento fora do raio"
                      desc="Ao habilitar, o técnico poderá iniciar o atendimento mesmo fora do raio configurado"
                    >
                      <Pcs2Switch
                        checked={permiteForaDoRaio}
                        onChange={handleSemRaioChange}
                        disabled={isSaving}
                        ariaLabel="Permitir atendimento fora do raio"
                      />
                    </Pcs2SettingRow>
                  </div>
                </Pcs2Subblock>
              </Pcs2Section>
            </>
          )}

          {tab === 'entrega' && (
            <Pcs2Section
              icon={IconPackageExport}
              title="Fluxo de entrega"
              desc="Configuração de status do processo de entrega pelo App Go"
            >
              <Pcs2Subblock
                title="Status do fluxo"
                desc="Defina o código de cada etapa do processo de entrega (2 caracteres alfanuméricos)"
              >
                <div className="pcs2-card">
                  <div className="pcs2-list">
                    {FLUXO_FIELDS.map((field) => (
                      <Pcs2ListItem
                        key={field.key}
                        id={`go-proc-entrega-${field.key}`}
                        icon={field.Icon}
                        label={field.label}
                        value={draft.fluxo_entrega[field.key]}
                        onChange={handleChange('fluxo_entrega', field)}
                        disabled={isSaving}
                        maxLength={field.len}
                      />
                    ))}
                  </div>
                </div>
              </Pcs2Subblock>
            </Pcs2Section>
          )}

          {tab === 'retirada' && (
            <Pcs2Section
              icon={IconPackageImport}
              title="Fluxo de retirada"
              desc="Configuração de status do processo de retirada de equipamentos pelo App Go"
            >
              <Pcs2Subblock
                title="Status do fluxo"
                desc="Defina o código de cada etapa do processo de retirada (2 caracteres alfanuméricos)"
              >
                <div className="pcs2-card">
                  <div className="pcs2-list">
                    {FLUXO_RETIRADA_FIELDS.map((field) => (
                      <Pcs2ListItem
                        key={field.key}
                        id={`go-proc-retirada-${field.key}`}
                        icon={field.Icon}
                        label={field.label}
                        info={field.info}
                        value={draft.fluxo_retirada[field.key]}
                        onChange={handleChange('fluxo_retirada', field)}
                        disabled={isSaving}
                        maxLength={field.len}
                      />
                    ))}
                  </div>
                </div>
              </Pcs2Subblock>
            </Pcs2Section>
          )}

          {tab === 'kit' && (
            <>
              <Pcs2Section
                icon={IconPackages}
                title="Kit técnico"
                desc="Configuração de processos para Kit técnico"
              >
                <Pcs2Subblock
                  title="Parâmetros e status"
                  desc="Códigos usados na abertura e andamento do Kit técnico no App Go"
                >
                  <div className="pcs2-card">
                    <div className="pcs2-list">
                      {KIT_FIELDS.map((field) => (
                        <Pcs2ListItem
                          key={field.key}
                          id={`go-proc-kit-${field.key}`}
                          icon={field.Icon}
                          label={field.label}
                          stockTag={field.stockTag}
                          info={field.info}
                          value={draft.kit_tecnico[field.key]}
                          onChange={handleChange('kit_tecnico', field)}
                          disabled={isSaving}
                          maxLength={field.len}
                          ariaLabel={fieldAriaLabel(field)}
                        />
                      ))}
                    </div>
                  </div>
                </Pcs2Subblock>
              </Pcs2Section>

              <hr className="pcs2-divider" />

              <Pcs2Section
                icon={IconReceipt}
                title="Pedido ao cliente"
                desc="Configurações dos pedidos de custo ao cliente, de peças gerado no uso."
              >
                <Pcs2Subblock
                  title="Parâmetros"
                  desc="Defina os códigos usados na abertura do pedido de custo ao cliente"
                >
                  <div className="pcs2-card">
                    <div className="pcs2-list">
                      {PEDIDO_CLIENTE_FIELDS.map((field) => (
                        <Pcs2ListItem
                          key={field.key}
                          id={`go-proc-pedido-${field.key}`}
                          icon={field.Icon}
                          label={field.label}
                          value={draft.pedido_cliente[field.key]}
                          onChange={handleChange('pedido_cliente', field)}
                          disabled={isSaving}
                          maxLength={field.len}
                        />
                      ))}
                    </div>
                  </div>
                </Pcs2Subblock>
              </Pcs2Section>

              <hr className="pcs2-divider" />

              <Pcs2Section
                icon={IconArrowBackUp}
                title="Rollback de Kit"
                desc="Defina aqui a configuração do processo de entrada dos itens do kit, quando retirados em quantidade máxima ou reduzida a zero, para o processo de entrada."
              >
                <Pcs2Subblock
                  title="Parâmetros"
                  desc="Códigos usados na abertura do pedido de entrada do rollback"
                >
                  <div className="pcs2-card">
                    <div className="pcs2-list">
                      {ROLLBACK_KIT_FIELDS.map((field) => (
                        <Pcs2ListItem
                          key={field.key}
                          id={`go-proc-rollback-${field.key}`}
                          icon={field.Icon}
                          label={field.label}
                          info={field.info}
                          value={draft.rollback_kit[field.key]}
                          onChange={handleChange('rollback_kit', field)}
                          disabled={isSaving}
                          maxLength={field.len}
                        />
                      ))}
                    </div>
                  </div>
                </Pcs2Subblock>
              </Pcs2Section>
            </>
          )}

          {tab === 'estoque' && (
            <Pcs2Section
              icon={IconBuildingWarehouse}
              title="Estoque"
              desc="Configuração de processos de estoque pelo App Go"
            >
              <Pcs2Subblock
                title="Conferência na coleta"
                desc="Define como os produtos são conferidos na recepção da coleta no App Go"
              >
                <div className="pcs2-card pcs2-card--flat">
                  <Pcs2SettingRow
                    title="Conferir pelo documento (pedido ou NF)"
                    desc="Ao habilitar, uma leitura do número do pedido ou da nota fiscal (chave DANFE ou QR) confere o pedido inteiro. As séries continuam sendo lidas e a retirada é sempre por item. Desabilitado, cada item e cada série são lidos."
                  >
                    <Pcs2Switch
                      checked={conferenciaPorDocumento}
                      onChange={handleModoConferenciaChange}
                      disabled={isSaving}
                      ariaLabel="Conferir pelo documento (pedido ou NF)"
                    />
                  </Pcs2SettingRow>
                </div>
              </Pcs2Subblock>
            </Pcs2Section>
          )}
        </div>
      </section>

      {Toaster}

      {typeof document !== 'undefined'
        ? createPortal(
            <button
              type="button"
              className="pcs2-save-fab"
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              aria-label="Salvar configuração"
            >
              {isSaving ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
              {isSaving ? 'Salvando...' : 'Salvar configuração'}
            </button>,
            document.body
          )
        : null}
    </div>
  );
}
